import * as React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import classNames from 'classnames';
import { Theme } from 'helpers/theme';
import UniversalSearch from 'components/UniversalSearch';
import searchSections, {
  SearchAction
} from 'components/UniversalSearch/SearchSections';
import { graphql } from 'relay-runtime';
import { createFragmentContainer } from 'react-relay';
import { HeaderMenu_manager } from './__generated__/HeaderMenu_manager.graphql';
import { useBookingModalDispatch } from 'views/BookingModalProvider';
import { useDelegateDetailsDispatch } from 'views/DelegateDetailsProvider';
import RequestBackgroundCheckLoader from 'features/background-checks/components/RequestBackgroundCheckLoader';
import {
  faCirclePlus,
  faGraduationCap
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const useStyles = createUseStyles((theme: Theme) => ({
  root: {
    background: 'white',
    width: '100%',
    height: 65,
    borderBottom: '1px solid #ededed',
    display: 'flex',
    alignItems: 'center',
    padding: '0px 14px'
  },
  content: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginRight: 44
  },
  leftItems: {
    minWidth: 340
  },
  rightItems: {},
  search: {
    position: 'absolute',
    top: 13,
    width: 320
  },
  mainButtons: {
    display: 'flex',
    alignItems: 'center',
    gridGap: theme.spacing(1),
    flexShrink: 0
  },
  mainButton: {
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    fontSize: 14,
    cursor: 'pointer',
    padding: 10,
    background: 'white',
    border: '1px solid #ededed',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
    borderRadius: theme.primaryBorderRadius,
    '&:hover': {
      backgroundColor: '#ebebec'
    },
    transition: 'background-color 0.15s'
  },
  bookingButton: {
    background: '#0b57ff',
    color: theme.colors.primaryWhite,
    border: '1px solid rgba(255, 255, 255, 0.18)',
    boxShadow:
      '0 1px 2px rgba(11, 87, 255, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.12)',
    '& $icon': {
      color: theme.colors.primaryWhite
    },
    '&:hover': {
      backgroundColor: '#0a4cdf'
    }
  },
  divider: {
    width: 1,
    height: 40,
    backgroundColor: '#ededed'
  },
  icon: {
    fontSize: 15,
    color: theme.colors.primaryBlack,
    paddingRight: 5
  }
}));

type Props = {
  className?: string;
  manager: HeaderMenu_manager;
  onEditDetails: () => void;
  onEditCompany: () => void;
};

function HeaderMenu({
  className,
  manager,
  onEditDetails,
  onEditCompany
}: Props) {
  const theme = useTheme();
  const classes = useStyles({ theme });

  const [isRequestCheckOpen, setRequestCheckOpen] = React.useState(false);

  const bookingModalDispatch = useBookingModalDispatch();
  const openBookingModal = () => {
    bookingModalDispatch({
      type: 'show'
    });
  };

  const delegateModalDispatch = useDelegateDetailsDispatch();
  const openDelegateSlideIn = () => {
    delegateModalDispatch({
      type: 'show'
    });
  };

  const searchActions: SearchAction[] = [
    {
      name: 'Add Delegate',
      keywords: ['add delegate', 'new delegate', 'create delegate'],
      onSelect: openDelegateSlideIn
    },
    {
      name: 'Edit My Details',
      keywords: ['edit manager', 'my details', 'profile', 'account'],
      onSelect: onEditDetails
    },
    {
      name: 'Edit Company Details',
      keywords: ['edit company', 'company details', 'organisation'],
      onSelect: onEditCompany
    },
    ...(manager.hasComplianceAccess
      ? [
          {
            name: 'Request Background Check',
            keywords: [
              'background check',
              'request check',
              'new check',
              'dbs',
              'compliance'
            ],
            onSelect: () => setRequestCheckOpen(true)
          }
        ]
      : [])
  ];

  return (
    <header className={classNames(classes.root, className)}>
      <div className={classes.content}>
        <div className={classes.leftItems}>
          <div className={classes.search}>
            <UniversalSearch
              companyName={manager.company.name}
              searchFunction={(text: string) =>
                searchSections(text, manager.hasComplianceAccess, searchActions)
              }
            />
          </div>
        </div>
        <div className={classes.rightItems}>
          <div className={classes.mainButtons}>
            <div
              className={classNames(classes.mainButton, classes.bookingButton)}
              onClick={openBookingModal}
            >
              <FontAwesomeIcon
                icon={faGraduationCap}
                className={classes.icon}
              />
              Booking
            </div>
            <div className={classes.divider} />
            <div className={classes.mainButton} onClick={openDelegateSlideIn}>
              <FontAwesomeIcon icon={faCirclePlus} className={classes.icon} />
              Add Delegate
            </div>
          </div>
        </div>
      </div>
      {isRequestCheckOpen && (
        <RequestBackgroundCheckLoader
          onClose={() => setRequestCheckOpen(false)}
        />
      )}
    </header>
  );
}

export default createFragmentContainer(HeaderMenu, {
  manager: graphql`
    fragment HeaderMenu_manager on Manager {
      hasComplianceAccess
      company {
        name
      }
    }
  `
});
